import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { MyAccountStoreProduct } from "./types";

import { clientConfig } from "../../../../config";
import { MyAccountStoreClientConfig } from "./types";
import { useMyAccountStoreProducts } from "./hooks/useMyAccountStoreProducts";
import { useMyAccountStoreFilters } from "./hooks/useMyAccountStoreFilters";
import { orderNowRedirect } from "./utils/orderNowRedirect";

import MyAccountStoreHeader from "./components/MyAccountStoreHeader";
import MyAccountStoreFilters from "./components/MyAccountStoreFilters";
import MyAccountStoreProductGrid from "./components/MyAccountStoreProductGrid";
import MyAccountStoreTagPopup from "./components/MyAccountStoreTagPopup";
import MyAccountStoreHowItWorks from "./components/MyAccountStoreHowItWorks";
import MyAccountStoreFaq from "./components/MyAccountStoreFaq";

interface Props {
  config: MyAccountStoreClientConfig;
}

interface OutletShape {
  setActiveMenuLinkId: (id: string) => void;
  country: string;
  language: string;
  setLoading: (b: boolean) => void;
  setError: (n: number) => void;
  asideBarData?: any;
}

const MyAccountStore: React.FC<Props> = ({ config }) => {
  const outlet = useOutletContext<OutletShape>();
  const { setActiveMenuLinkId, country, language, setLoading } = outlet;

  const navigate = useNavigate();

  const isCountryAllowed = config.allowedCountries.includes(country);

  const { loading, error, products } = useMyAccountStoreProducts({
    storeKey: config.storeKey,
    country,
    enabled: isCountryAllowed,
  });

  const {
    selectedByDimension,
    toggleValue,
    clearAll,
    filteredProducts,
    hasActiveFilters,
    valuesByDimension,
    countsByDimension,
  } = useMyAccountStoreFilters({
    products,
    dimensions: config.filterDimensions,
  });

  const [orderingProductID, setOrderingProductID] = useState<number | null>(null);
  const [activePopup, setActivePopup] = useState<MyAccountStoreProduct | null>(null);

  useEffect(() => {
    setActiveMenuLinkId(config.menuLinkId);
    if (!isCountryAllowed) {
      navigate(`/${country}/account/my-orders`, { replace: true });
    }
  }, [config.menuLinkId, isCountryAllowed, country, navigate, setActiveMenuLinkId]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  if (!isCountryAllowed) return null;
  if (error) return null;

  const handleOrderNow = (product: MyAccountStoreProduct): void => {
    if (orderingProductID !== null) return;
    setOrderingProductID(product.productID);
    orderNowRedirect({
      product,
      country,
      language,
    }).catch((err) => {
      (window as any).clientLogger?.error?.("MyAccountStore: orderNowRedirect failed", err);
      setOrderingProductID(null);
    });
  };

  const handleMoreInfo = (product: MyAccountStoreProduct): void => {
    if (!product.url) return;
    // These products have no funnel product page — the detail page lives on the
    // CMS / main website. `product.url` is the route-alias path (already locale-
    // prefixed, e.g. "/de/frauen-gesundheitsprofil"), same as the CMS renders it
    // (OtcProductUtils.formatProductForDisplay). Prefix the main-website host so
    // we leave the funnel and land on the real CMS product page.
    window.location.href = `${clientConfig.MAIN_WEBSITE_URL}${product.url}`;
  };

  const handleTagClick = (product: MyAccountStoreProduct): void => {
    setActivePopup(product);
  };

  return (
    <div className="account-content my-account-store">
      <div className="px-4 md:px-0">
        <MyAccountStoreHeader language={language} translationPrefix={config.translationPrefix} />

        <MyAccountStoreFilters
          language={language}
          translationPrefix={config.translationPrefix}
          dimensions={config.filterDimensions}
          valuesByDimension={valuesByDimension}
          countsByDimension={countsByDimension}
          selectedByDimension={selectedByDimension}
          onToggle={toggleValue}
          onClearAll={clearAll}
        />

        <MyAccountStoreProductGrid
          language={language}
          translationPrefix={config.translationPrefix}
          products={filteredProducts}
          tagDimensions={config.card.tagDimensions}
          collapsedCount={config.collapsedProductCount}
          hasActiveFilters={hasActiveFilters}
          orderingProductID={orderingProductID}
          onOrderNow={handleOrderNow}
          onMoreInfo={handleMoreInfo}
          onTagClick={handleTagClick}
        />

        <MyAccountStoreHowItWorks
          language={language}
          titleTranslationKey={config.howItWorks.titleTranslationKey}
          steps={config.howItWorks.steps}
        />

        <MyAccountStoreFaq
          language={language}
          titleTranslationKey={config.faq.titleTranslationKey}
          items={config.faq.items}
        />
      </div>

      {activePopup && (
        <MyAccountStoreTagPopup
          language={language}
          translationPrefix={config.translationPrefix}
          product={activePopup}
          tagDimensions={config.card.tagDimensions}
          onClose={() => setActivePopup(null)}
        />
      )}
    </div>
  );
};

export default MyAccountStore;
